/**
 * @fileoverview Deterministic field-level conflict policy for local-first synchronization.
 * @module local-db/replication/conflictResolver
 *
 * Responsibilities: - Detect meaningful field differences - Apply collection-aware merge rules (keyed/_id union, numeric max, latest-timestamp, server/client-wins, canonical nested merges) - Surface protected complex topology conflicts for review
 * Dependencies:     - Plain serialized documents; `@firesquirrel/sync-config` (embedded building-system merge helper)
 * Architecture:     - Pure conflict policy shared by replication callers; no Dexie or network access
 * Notes:            - Complex anchor, membership, contact-origin, and visit topology is never silently merged outside the operation boundary
 *                   - `systems` merges per item keyed by `systemId` via the shared embedded merge helper and must preserve `_deleted` tombstones (G-009)
 */

import {
  caseStatusRank,
  mergeCaseEventCollision,
  mergeEmbeddedBuildingSystem,
} from '@firesquirrel/sync-config';

/**
 * @contract FieldDiff
 * Purpose: One top-level local/server field difference and its merge decision.
 * Fields: Field name, both values, optional rule, merge status, and merged value.
 * Rules: Missing autoMergeRule means the conflict must be surfaced for review.
 * Sync: Ephemeral replication decision; not persisted directly.
 */
export interface FieldDiff {
  field: string;
  localValue: unknown;
  serverValue: unknown;
  autoMergeRule?: 'union' | 'max' | 'latest' | 'server-wins' | 'client-wins' | 'nested-merge';
  merged?: boolean;
  mergedValue?: unknown;
}

/**
 * @contract ConflictResolution
 * Purpose: Supported terminal resolution classifications for surfaced sync conflicts.
 * Fields: Keep-local, accept-server, field-level merge, or auto-merged.
 * Rules: Protected complex topology cannot be marked auto-merged by this resolver.
 * Sync: Stored with conflict metadata when the host persists a resolution.
 */
export type ConflictResolution = 
  | 'keep-local' 
  | 'accept-server' 
  | 'field-level-merge' 
  | 'auto-merged';

/**
 * @contract SyncConflict
 * Purpose: Persistable conflict record containing both states and review metadata.
 * Fields: Identity, collection/document, local/server snapshots, lifecycle, and field differences.
 * Rules: Resolution metadata is absent until resolved; protected complex differences require explicit review.
 * Sync: Local conflict state associated with a server push conflict.
 */
export interface SyncConflict {
  id: string;
  collection: string;
  documentId: string;
  localState: Record<string, unknown>;
  serverState: Record<string, unknown>;
  detectedAt: string;
  resolved: boolean;
  resolution?: ConflictResolution;
  resolvedBy?: string; // userId
  resolvedAt?: string;
  autoMerged?: boolean;
  fieldDiffs: FieldDiff[];
}

// ---------------------------------------------------------------------------
// Auto-merge rule definitions by field type
// ---------------------------------------------------------------------------

const ARRAY_FIELDS = new Set([
  'photos', 'contacts', 'linkedEntities', 'violations', 'events',
  'tags', 'attachments', 'notes',
  // G-017: contact sub-document arrays — union merge preserves additions
  // from both sides when the same contact is edited concurrently.
  'affiliations', 'defaultRoles',
]);

// Array fields where items are identified by a domain key other than _id/tempId.
// Value = the property name used to deduplicate items during union merge.
const KEYED_ARRAY_FIELDS: Record<string, string> = {
  departmentMemberships:   'departmentId',
  organizationMemberships: 'organizationId',
  phoneNumbers:            '_id',
  emails:                  'email',   // G-017: dedup by email address on merge
  identities:              'issuerAssignedId',
  serviceAreas:            'typeCode',
  contacts:                'contactId',
  events:                  'eventIndex',
  // G-009: embedded building systems merge per item via the shared
  // mergeEmbeddedBuildingSystem helper — keyed by systemId, tombstones
  // dominate, child arrays union by identity (never resurrected, never
  // whole-array last-write-wins).
  systems:                 'systemId',
};

// Keyed arrays whose duplicate-key collisions merge per item instead of keeping
// the local item. Other keyed arrays keep the local item on collision.
const KEYED_ARRAY_COLLISION_MERGERS: Record<string, (local: any, server: any) => any> = {
  events: mergeCaseEventCollision,
};

const NUMERIC_COUNTER_FIELDS = new Set([
  'violationCount', 'photoCount', 'eventCount', 'contactCount'
]);

const TIMESTAMP_FIELDS = new Set([
  'lastInspectionDate', 'completedAt', 'scheduledDate', 'dueDate',
  'createdAt', 'updatedAt', 'lastModifiedAt', 'lastActivityDate', 
  'lastActivityTimestamp'
]);

const SERVER_AUTHORITATIVE_FIELDS = new Set([
  'status', 'priority', 'violationCodeId', 'templateId', 'caseType',
  'inspectionAccountId', 'assignedTo'
]);

const CLIENT_AUTHORITATIVE_FIELDS = new Set([
  'inspectorNotes', 'fieldObservations', 'localAnnotations',
  // User-edited location/structural details — local edits win over re-import float drift
  'entityLocation', 'buildingDetails', 'parcelDetails', 'hydrantDetails',
]);

// ---------------------------------------------------------------------------
// Phase 2A canonical parcel sub-fields carve-out.
//
// `parcelDetails` is otherwise whole-object client-authoritative (see
// CLIENT_AUTHORITATIVE_FIELDS above) so a user's manual field correction
// (e.g. yearBuilt) survives a re-import's float drift. But Phase 2A added
// NEW import-only canonical sub-blocks inside `parcelDetails`
// (structureSummary, taxAssessment, legalReferences, geographicReferences,
// parcelSquareFeet, lastOwnershipTransferDate) that have no pre-existing
// "user edit" use case to protect. Without this carve-out, a stale mobile
// client that cached `parcelDetails` before a Regrid refresh or the
// Phase 2A.3 backfill would silently overwrite the server's freshly
// canonical data the next time an unrelated field on the same document
// conflicts — the exact risk flagged in
// Docs/duckdb-geo-import-phase0-audit/11-parcel-owner-migration-review.md
// §8.3 ("avoid overwriting newer server ownership data with stale
// legacy-shaped local documents"). These sub-keys are carved out to
// server-wins while every other `parcelDetails` sub-field (acres,
// yearBuilt, bathCount, ownerName, etc.) keeps its existing client-wins
// behavior unchanged.
// ---------------------------------------------------------------------------
const CANONICAL_IMPORT_SUBFIELDS: Record<string, Set<string>> = {
  parcelDetails: new Set([
    'structureSummary', 'taxAssessment', 'legalReferences', 'geographicReferences',
    'parcelSquareFeet', 'lastOwnershipTransferDate',
  ]),
};

// ---------------------------------------------------------------------------
// Per-collection fields excluded from conflict detection.
// Two reasons to exclude a field:
//   1. Server pull projection strips it (e.g. vendorData.rawHash) — the local
//      copy always has it, the server copy never returns it → false conflict.
//   2. Mobile-local denormalized field (e.g. inspectionAccountName) — written
//      by mobile for display purposes, never stored on the server document.
// ---------------------------------------------------------------------------
const COLLECTION_EXCLUDED_FIELDS: Record<string, Set<string>> = {
  entities: new Set([
    'inspectionAccountName',   // mobile-local denormalized display field
    'zoning',                  // mobile-local field not in server schema
  ]),
};

// Top-level fields where nested sub-keys are excluded (projection strips them)
const VENDORDATA_EXCLUDED_SUBKEYS = new Set(['rawHash', 'rawPayload']);

function shouldExcludeField(collection: string, field: string): boolean {
  return COLLECTION_EXCLUDED_FIELDS[collection]?.has(field) ?? false;
}

function canonicalCollection(collection?: string): string {
  if (collection === 'inspectableEntities') return 'entities';
  if (collection === 'inspectableEntityGroups') return 'complex_entity_groups';
  if (collection === 'inspectionCases') return 'cases';
  return collection ?? '';
}

function normalizeProtectedValue(value: unknown): unknown {
  if (value && typeof (value as any).toHexString === 'function') return (value as any).toHexString();
  if (Array.isArray(value)) return value.map(normalizeProtectedValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, normalizeProtectedValue(nested)]),
    );
  }
  return value;
}

function complexContactOriginProjection(value: unknown): unknown[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((contact: any, index) => {
    const origins = Array.isArray(contact?.linkOrigins)
      ? contact.linkOrigins.filter((origin: any) => origin?.originType === 'complex' || origin?.groupId !== undefined)
      : [];
    return origins.length === 0 ? [] : [{
      contactId: String(contact?.contactId ?? contact?._id ?? contact?.tempId ?? index),
      linkOrigins: normalizeProtectedValue(origins),
    }];
  }).sort((a: any, b: any) => a.contactId.localeCompare(b.contactId));
}

function complexVisitProjection(value: unknown): unknown[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((event: any, index) => {
    if (event?.complexVisitManifest === undefined && event?.complexVisitReference === undefined) return [];
    return [{
      eventIndex: event?.eventIndex ?? index,
      complexVisitManifest: normalizeProtectedValue(event?.complexVisitManifest),
      complexVisitReference: normalizeProtectedValue(event?.complexVisitReference),
    }];
  });
}

function protectedProjectionChanged(localValue: unknown, serverValue: unknown, project: (value: unknown) => unknown): boolean {
  return JSON.stringify(project(localValue)) !== JSON.stringify(project(serverValue));
}

function isProtectedComplexConflict(
  collection: string | undefined,
  field: string,
  localValue: unknown,
  serverValue: unknown,
): boolean {
  const canonical = canonicalCollection(collection);
  if (canonical === 'entities') {
    if (field === 'complexAnchor' || field === 'complexMembership') return true;
    if (field === 'contacts') return protectedProjectionChanged(localValue, serverValue, complexContactOriginProjection);
  }
  if (canonical === 'complex_entity_groups') {
    return field === 'anchor' || field === 'anchorHistory' || field === 'status' || field === 'contacts' || field === '_deleted';
  }
  return canonical === 'cases' && field === 'events' &&
    protectedProjectionChanged(localValue, serverValue, complexVisitProjection);
}

/**
 * @process detectFieldDiffs
 * Steps: Compare normalized top-level fields, exclude projection-only values, and assign collection-aware merge rules.
 * Inputs: Local/server documents and optional canonical or Dexie collection name.
 * Outputs: Ordered field differences; protected topology differences have no automatic rule.
 * Failure Handling: Unknown fields default to server-wins; malformed arrays remain comparable values.
 * Idempotency: REQUIRED — pure derivation.
 */
export function detectFieldDiffs(
  localState: Record<string, unknown>,
  serverState: Record<string, unknown>,
  collection?: string
): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  const allKeys = new Set([
    ...Object.keys(localState),
    ...Object.keys(serverState)
  ]);

  for (const field of allKeys) {
    // Skip internal MongoDB fields EXCEPT _deleted.
    // _deleted must be included so the conflict resolver sees a delete intent
    // and applies 'client-wins' instead of silently reverting the delete.
    if (field.startsWith('_') && field !== '_id' && field !== '_deleted') continue;

    // Skip collection-specific excluded fields
    if (collection && shouldExcludeField(collection, field)) continue;

    let localValue = localState[field];
    let serverValue = serverState[field];

    // For vendorData, strip projected-away sub-keys before comparing
    if (field === 'vendorData' && localValue && typeof localValue === 'object' && !Array.isArray(localValue)) {
      localValue = Object.fromEntries(
        Object.entries(localValue as Record<string, unknown>).filter(([k]) => !VENDORDATA_EXCLUDED_SUBKEYS.has(k))
      );
      serverValue = serverValue && typeof serverValue === 'object' && !Array.isArray(serverValue)
        ? Object.fromEntries(
            Object.entries(serverValue as Record<string, unknown>).filter(([k]) => !VENDORDATA_EXCLUDED_SUBKEYS.has(k))
          )
        : serverValue;
    }

    // Normalize array ordering before comparison so identical arrays in different
    // order (e.g. serviceAreas reordered by an import job) don't produce false diffs.
    const normalizeForCompare = (v: unknown): unknown => {
      if (!Array.isArray(v)) return v;
      const domainKey = field in KEYED_ARRAY_FIELDS ? KEYED_ARRAY_FIELDS[field] : null;
      const idKey = domainKey ?? '_id';
      const hasKey = v.length > 0 && v[0] != null && typeof v[0] === 'object' && idKey in (v[0] as object);
      if (!hasKey) return v;
      return [...v].sort((a: any, b: any) => {
        const ak = String(a[idKey] ?? '');
        const bk = String(b[idKey] ?? '');
        return ak < bk ? -1 : ak > bk ? 1 : 0;
      });
    };
    const normLocal = normalizeForCompare(localValue);
    const normServer = normalizeForCompare(serverValue);

    // Deep equality check
    if (JSON.stringify(normLocal) !== JSON.stringify(normServer)) {
      const autoMergeRule = determineAutoMergeRule(field, localValue, serverValue, collection);
      
      diffs.push({
        field,
        localValue,
        serverValue,
        autoMergeRule,
        merged: false,
      });
    }
  }

  return diffs;
}

// ---------------------------------------------------------------------------
// Determine auto-merge rule for a field
// ---------------------------------------------------------------------------
function determineAutoMergeRule(
  field: string,
  localValue: unknown,
  serverValue: unknown,
  collection?: string,
): FieldDiff['autoMergeRule'] {
  if (isProtectedComplexConflict(collection, field, localValue, serverValue)) {
    return undefined;
  }

  // Keyed array fields (membership arrays with domain-specific identity keys): union merge
  if (field in KEYED_ARRAY_FIELDS && Array.isArray(localValue) && Array.isArray(serverValue)) {
    return 'union';
  }

  // Array fields: union merge
  if (ARRAY_FIELDS.has(field) && Array.isArray(localValue) && Array.isArray(serverValue)) {
    return 'union';
  }

  // Numeric counters: max value wins
  if (NUMERIC_COUNTER_FIELDS.has(field) && 
      typeof localValue === 'number' && 
      typeof serverValue === 'number') {
    return 'max';
  }

  // Timestamp fields: most recent wins
  if (TIMESTAMP_FIELDS.has(field) && 
      typeof localValue === 'string' && 
      typeof serverValue === 'string') {
    return 'latest';
  }

  // Server-authoritative fields
  if (SERVER_AUTHORITATIVE_FIELDS.has(field)) {
    return 'server-wins';
  }

  // Client-authoritative fields (inspector notes)
  if (CLIENT_AUTHORITATIVE_FIELDS.has(field)) {
    // Carve-out: fields with a canonical-import sub-block (see
    // CANONICAL_IMPORT_SUBFIELDS) merge at the sub-key level instead of
    // treating the whole object as client-authoritative.
    const canonicalKeys = CANONICAL_IMPORT_SUBFIELDS[field];
    const isPlainObject = (v: unknown): v is Record<string, unknown> =>
      !!v && typeof v === 'object' && !Array.isArray(v);
    if (canonicalKeys && isPlainObject(localValue) && isPlainObject(serverValue)) {
      return 'nested-merge';
    }
    return 'client-wins';
  }

  // _deleted: if local is deleting the doc, client intent wins unconditionally.
  // This prevents the conflict resolver from silently reverting a delete by
  // merging to an empty diff set (canAutoMerge([]) = true → writes server non-deleted doc).
  if (field === '_deleted' && localValue === true) {
    return 'client-wins';
  }

  // Default: server-wins for any unrecognized field.
  // When the server is strictly newer (the only code path that reaches here),
  // unknown fields should silently accept the server state rather than surface
  // a manual conflict to the user. A "genuine conflict" requiring human review
  // only exists when two users concurrently edit the same scalar — but we have
  // no way to distinguish that from a normal server update at this layer.
  // Erring toward server-wins is correct: the server is the source of truth,
  // and any client edit will re-push with a newer timestamp on the next cycle.
  return 'server-wins';
}

/**
 * @process applyAutoMergeRules
 * Steps: Apply union, max, latest, authority, or nested-object strategy to each eligible difference.
 * Inputs: Field differences from detectFieldDiffs.
 * Outputs: New differences annotated with merged status/value.
 * Failure Handling: Differences without a rule remain unresolved for review.
 * Idempotency: REQUIRED — pure derivation.
 */
export function applyAutoMergeRules(diffs: FieldDiff[]): FieldDiff[] {
  return diffs.map(diff => {
    if (!diff.autoMergeRule) return diff;

    let mergedValue: unknown;
    let merged = false;

    switch (diff.autoMergeRule) {
      case 'union': {
        const domainKey = KEYED_ARRAY_FIELDS[diff.field];
        mergedValue = diff.field === 'systems'
          ? mergeEmbeddedSystemsArray(diff.localValue as any[], diff.serverValue as any[])
          : domainKey
            ? mergeKeyedArrays(
                diff.localValue as any[],
                diff.serverValue as any[],
                domainKey,
                KEYED_ARRAY_COLLISION_MERGERS[diff.field],
              )
            : mergeArrays(diff.localValue as any[], diff.serverValue as any[]);
        merged = true;
        break;
      }

      case 'max':
        mergedValue = Math.max(diff.localValue as number, diff.serverValue as number);
        merged = true;
        break;

      case 'latest':
        mergedValue = new Date(diff.localValue as string) > new Date(diff.serverValue as string)
          ? diff.localValue
          : diff.serverValue;
        merged = true;
        break;

      case 'server-wins':
        mergedValue = diff.serverValue;
        merged = true;
        break;

      case 'client-wins':
        mergedValue = diff.localValue;
        merged = true;
        break;

      case 'nested-merge': {
        // Start from the client-authoritative object, then overwrite only
        // the canonical import sub-keys with the server's values (server
        // wins for those keys; every other key stays client-authoritative).
        const canonicalKeys = CANONICAL_IMPORT_SUBFIELDS[diff.field] ?? new Set<string>();
        const localObj = (diff.localValue && typeof diff.localValue === 'object' && !Array.isArray(diff.localValue))
          ? diff.localValue as Record<string, unknown>
          : {};
        const serverObj = (diff.serverValue && typeof diff.serverValue === 'object' && !Array.isArray(diff.serverValue))
          ? diff.serverValue as Record<string, unknown>
          : {};
        const combined: Record<string, unknown> = { ...localObj };
        for (const key of canonicalKeys) {
          if (key in serverObj) combined[key] = serverObj[key];
          else delete combined[key];
        }
        mergedValue = combined;
        merged = true;
        break;
      }
    }

    return { ...diff, merged, mergedValue };
  });
}

// ---------------------------------------------------------------------------
// Apply registered collision-merged field values (monotonic merges only)
// ---------------------------------------------------------------------------
/**
 * @process applyCollisionMergedFields
 * Steps: Copy `base`; for each diff whose field has a registered keyed-array
 *   collision merger (currently `events` via the case-event lifecycle merge),
 *   apply the precomputed mergedValue.
 * Inputs: Base document and diffs already processed by applyAutoMergeRules.
 * Outputs: { doc, appliedFields } — merged document and the field names applied.
 * Failure Handling: Unmerged diffs and fields without a collision merger are skipped.
 * Idempotency: REQUIRED — pure derivation.
 *
 * Registered collision mergers are monotonic by contract: they can only
 * advance state (a completed case event can never downgrade to inProgress)
 * and union in server items, so applying them is safe even on the
 * client-wins path where scalar/server-wins diffs must keep local values.
 */
export function applyCollisionMergedFields(
  base: Record<string, unknown>,
  diffs: FieldDiff[],
): { doc: Record<string, unknown>; appliedFields: string[] } {
  const appliedFields: string[] = [];
  const doc: Record<string, unknown> = { ...base };
  for (const diff of diffs) {
    if (!diff.merged || diff.mergedValue === undefined) continue;
    if (!KEYED_ARRAY_COLLISION_MERGERS[diff.field]) continue;
    doc[diff.field] = diff.mergedValue;
    appliedFields.push(diff.field);
  }
  return { doc, appliedFields };
}

// ---------------------------------------------------------------------------
// Clock-safe timestamp bump
// ---------------------------------------------------------------------------
/**
 * @process isoAfterBoth
 * Steps: Parse both timestamps; return an ISO string 1 ms after the later of
 *   the two, falling back to wall-clock time when parsing fails. Guarantees
 *   the result is strictly greater than both inputs so timestamp arbitration
 *   accepts the re-pushed merged document even when the browser clock is
 *   behind the server clock or was corrected backward.
 * Inputs: Two ISO timestamp strings.
 * Outputs: ISO string strictly greater than both.
 * Failure Handling: If either string is unparseable, uses Date.now() as floor.
 * Idempotency: REQUIRED — pure derivation.
 */
export function isoAfterBoth(localTs: string, serverTs: string): string {
  const localMs = Date.parse(localTs);
  const serverMs = Date.parse(serverTs);
  const maxMs = Math.max(
    isNaN(localMs) ? 0 : localMs,
    isNaN(serverMs) ? 0 : serverMs,
    Date.now(),
  );
  return new Date(maxMs + 1).toISOString();
}

// ---------------------------------------------------------------------------
// Client-wins conflict decision (replication loop, local-newer-or-equal rule)
// ---------------------------------------------------------------------------
/**
 * @process resolveClientNewerConflictDoc
 * Steps: Apply registered collision merges to the local doc. When events
 *   converged, also reconcile the case-level status if the server carries a
 *   more advanced one (e.g. active -> caseCompleted). Stamp an updatedAt
 *   strictly after both conflict timestamps (via isoAfterBoth) so the retry
 *   cannot be rejected by clock skew. Otherwise return the local doc unchanged
 *   (client-wins semantics).
 * Inputs: Local doc, server doc (conflict), diffs processed by applyAutoMergeRules.
 * Outputs: { docToStore, requeued }.
 * Failure Handling: No collision-merged diffs means the local doc is returned as-is.
 * Idempotency: REQUIRED — pure derivation; callers own persistence and requeue.
 *
 * Rationale: the server's stale-write guard rejects lifecycle regressions
 * regardless of client clock. Without convergence here, a stale local
 * `inProgress` case would be re-pushed and rejected every cycle while the
 * doc sits below the push checkpoint — permanently stale. Scalar and
 * server-wins diffs deliberately keep local values on this path.
 */
export function resolveClientNewerConflictDoc(
  localDoc: Record<string, unknown>,
  serverDoc: Record<string, unknown>,
  mergedDiffs: FieldDiff[],
  localTs: string,
  serverTs: string,
): { docToStore: Record<string, unknown>; requeued: boolean } {
  const { doc, appliedFields } = applyCollisionMergedFields(localDoc, mergedDiffs);
  if (appliedFields.length === 0) return { docToStore: localDoc, requeued: false };

  // When the server advanced an event (e.g. inProgress -> completed), the
  // case-level status it computed (e.g. active -> caseCompleted) must also
  // travel to the client or the re-pushed doc reopens a completed case.
  const eventsMerged = appliedFields.includes('events');
  const localRank = caseStatusRank(localDoc.caseStatus);
  const serverRank = caseStatusRank(serverDoc.caseStatus);
  const reconciledCaseStatus = eventsMerged && serverRank > localRank
    ? serverDoc.caseStatus
    : doc.caseStatus;

  return {
    docToStore: {
      ...doc,
      ...(reconciledCaseStatus !== undefined && { caseStatus: reconciledCaseStatus }),
      updatedAt: isoAfterBoth(localTs, serverTs),
    },
    requeued: true,
  };
}

// ---------------------------------------------------------------------------
// Merge two arrays with deduplication by _id or tempId
// ---------------------------------------------------------------------------
function mergeArrays(localArray: any[], serverArray: any[]): any[] {
  const merged = [...localArray];
  const seenIds = new Set(
    localArray
      .map(item => item._id || item.tempId)
      .filter(Boolean)
  );

  for (const serverItem of serverArray) {
    const id = serverItem._id || serverItem.tempId;
    if (!id || !seenIds.has(id)) {
      merged.push(serverItem);
      if (id) seenIds.add(id);
    }
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Merge two arrays with deduplication by a named domain key (e.g. departmentId)
// ---------------------------------------------------------------------------
function mergeKeyedArrays(
  localArray: any[],
  serverArray: any[],
  key: string,
  mergeCollision?: (local: any, server: any) => any,
): any[] {
  const identity = (item: any): string | undefined => {
    const value = item?.[key] ?? item?._id ?? item?.tempId;
    return value == null ? undefined : String(value);
  };
  const merged = [...localArray];
  const seenKeys = new Set(localArray.map(identity).filter(Boolean));

  for (const serverItem of serverArray) {
    const itemIdentity = identity(serverItem);
    if (!itemIdentity || !seenKeys.has(itemIdentity)) {
      merged.push(serverItem);
      if (itemIdentity) seenKeys.add(itemIdentity);
    } else if (mergeCollision) {
      const index = merged.findIndex((item) => identity(item) === itemIdentity);
      merged[index] = mergeCollision(merged[index], serverItem);
    }
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Embedded building systems — per-item merge (G-009).
// Colliding systemId pairs run through the shared mergeEmbeddedBuildingSystem
// (existing=local, incoming=server → server scalars win, local-only keys and
// child arrays preserved, tombstones dominate). Unkeyed/new items union in.
// ---------------------------------------------------------------------------
function mergeEmbeddedSystemsArray(localArray: any[], serverArray: any[]): any[] {
  const localKeys = new Set(
    localArray.map((i) => (i?.systemId != null ? String(i.systemId) : undefined)).filter(Boolean),
  );
  const serverByKey = new Map(
    serverArray
      .map((i) => (i?.systemId != null ? ([String(i.systemId), i] as const) : undefined))
      .filter((e): e is readonly [string, any] => Boolean(e)),
  );
  const merged = localArray.map((item) => {
    const k = item?.systemId != null ? String(item.systemId) : undefined;
    const serverItem = k ? serverByKey.get(k) : undefined;
    return serverItem ? mergeEmbeddedBuildingSystem(item, serverItem) : item;
  });
  for (const serverItem of serverArray) {
    const k = serverItem?.systemId != null ? String(serverItem.systemId) : undefined;
    if (!k || !localKeys.has(k)) merged.push(serverItem);
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Build merged document from field diffs
// ---------------------------------------------------------------------------
/**
 * @process buildMergedDocument
 * Steps: Clone the authoritative base and apply every successfully merged field value.
 * Inputs: Base server state and annotated differences.
 * Outputs: New merged document.
 * Failure Handling: Unresolved differences leave the base field unchanged.
 * Idempotency: REQUIRED — pure derivation.
 */
export function buildMergedDocument(
  baseState: Record<string, unknown>,
  diffs: FieldDiff[]
): Record<string, unknown> {
  const merged = { ...baseState };

  for (const diff of diffs) {
    if (diff.merged && diff.mergedValue !== undefined) {
      merged[diff.field] = diff.mergedValue;
    }
  }

  return merged;
}

/**
 * @process canAutoMerge
 * Steps: Require an explicit automatic rule for every difference.
 * Inputs: Field differences.
 * Outputs: Whether generic replication may merge without review.
 * Failure Handling: Any protected/unclassified difference returns false.
 * Idempotency: REQUIRED — pure predicate.
 */
export function canAutoMerge(diffs: FieldDiff[]): boolean {
  return diffs.every(diff => diff.autoMergeRule !== undefined);
}

/**
 * @process requiresManualConflictResolution
 * Steps: Ignore timestamp-only drift and detect any difference without an automatic rule.
 * Inputs: Collection-aware field differences.
 * Outputs: Whether replication must surface the conflict before timestamp arbitration.
 * Failure Handling: Protected/unclassified differences return true.
 * Idempotency: REQUIRED — pure predicate.
 */
export function requiresManualConflictResolution(diffs: FieldDiff[]): boolean {
  return !canAutoMerge(diffs.filter((diff) => diff.field !== 'updatedAt'));
}

/**
 * @process autoResolveConflict
 * Steps: Detect collection-aware differences, apply rules, reject protected conflicts, and build the merged document.
 * Inputs: Local/server states and optional collection identity.
 * Outputs: Merged state with decisions or null when explicit review is required.
 * Failure Handling: Protected complex topology returns null and is never silently accepted.
 * Idempotency: REQUIRED — pure derivation.
 */
export function autoResolveConflict(
  localState: Record<string, unknown>,
  serverState: Record<string, unknown>,
  collection?: string,
): { merged: Record<string, unknown>; diffs: FieldDiff[] } | null {
  const diffs = detectFieldDiffs(localState, serverState, collection);
  const mergedDiffs = applyAutoMergeRules(diffs);

  if (!canAutoMerge(diffs)) {
    return null; // Cannot auto-merge, requires manual resolution
  }

  const merged = buildMergedDocument(serverState, mergedDiffs);
  return { merged, diffs: mergedDiffs };
}
